import * as React from "react";
import { styled } from "@mui/material/styles";
import { Grid, Link, Typography } from "@mui/material";
import { AppBody } from "../../AppBody";
import Breadcrumbs from "../../../containers/Breadcrumbs";
import { appHeaderTitle } from "../../../common/services/Utilities";
import BackButton from "../../../containers/BackButton";
import { KeyValue, Query, SearchPillData } from "../../../interfaces";
import MarketShareSearchForm from "../../../containers/MarketShareSearchForm";
import SearchPills from "../../../containers/SearchPills";
import DepositsNameSearchTable from "../../../containers/DepositsNameSearchTable";
import MarketPresenceTables from "./MarketPresence/MarketPresenceTables";
import BackToResultsButton from "../../../containers/BackToResultsButton";
import DepositMarketShareCountySummaryByState from "../../../containers/DepositMarketShareCountySummaryByState";
import DepositMarketShareTable from "./DepositMarketShare/DepositMarketShareSelectedTable";
import GrowthRate from "../../../containers/GrowthRate";
import CloudFoundryBanner from "../../../containers/CloudFoundryBanner";
import ProFormaTable from "../../../containers/ProFormaTable";
import ShowHideButton from "../../ShowHideButton";

const PREFIX = "MarketShare";

const classes = {
  body: `${PREFIX}-body`,
  heading: `${PREFIX}-heading`,
  heading2: `${PREFIX}-heading2`,
  openCloseAll: `${PREFIX}-openCloseAll`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.body}`]: {
    color: theme.palette.primary.main,
    fontWeight: 550,
    fontSize: 16,
    lineHeight: "24px",
    fontStyle: "normal",
  },

  [`& .${classes.heading}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },

  [`& .${classes.heading2}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 18,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },

  [`& .${classes.openCloseAll}`]: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

interface Props {
  appState: Query;
  updateUi: (field: string, value: string | KeyValue[]) => void;
  updateFilter: (field: string, value: string | string[]) => void;
  updatePagination: () => void;
}

export default function MarketShare({
  appState,
  // setRouterHistory,
  updatePagination,
  updateUi,
}: Props) {
  const introParagraph = <>{appHeaderTitle("marketShare")}</>;

  const state = React.useMemo(() => {
    return {
      institutionType: appState.ui.institutionType,
      reportType: appState.ui.reportType,
      state: appState.filters.stalpbr,
    };
  }, [
    appState.filters.stalpbr,
    appState.ui.institutionType,
    appState.ui.reportType,
  ]);

  const [prevState, setPrevState] = React.useState({});

  React.useEffect(() => {
    if (
      appState.ui.reportType !== "proForma" &&
      appState.ui.partialMerger.length > 0
    ) {
      updateUi("partialMerger", []);
    }
  }, [appState.ui.partialMerger.length, appState.ui.reportType, updateUi]);

  React.useEffect(() => {
    if (JSON.stringify(prevState) !== JSON.stringify(state)) {
      setPrevState(state);
    }
  }, [prevState, state]);

  const marketShareSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState?.filters?.lastYear}`,
      static: true,
      formKey: "lastYear",
    },
    {
      label: "Cert",
      value: appState.filters.cert ? appState.filters.cert.toString() : "",
      static: true,
      formKey: "cert",
    },
    {
      label: "Name",
      value: appState.search.name ? appState.search.name.toString() : "",
      search: true,
      formKey: "name",
      static: true,
    },
    {
      label: "BHC RSSDHCR",
      value: appState.filters.bhcRSSD ? appState.filters.bhcRSSD : "",
      search: true,
      formKey: "bhcRSSD",
      static: true,
    },
    {
      label: "BHC Name",
      value: appState.search.bhcName ? appState.search.bhcName.toString() : "",
      search: true,
      formKey: "bhcName",
      static: true,
    },
    {
      label: "",
      value: appState.ui.displayAll ? "Show All States/Counties" : "",
      ui: true,
      static: true,
      formKey: "displayAll",
    },
    {
      label: "State",
      value:
        appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
          ? appState.filters.stalpbr
          : "",
      static: false,
      formKey: "stalpbr",
    },
    {
      label: "",
      value: appState.ui.displayResults === "msas" ? "MSAs" : "",
      ui: true,
      static: true,
      formKey: "displayResults",
    },
    {
      label: "MSA",
      value:
        appState.filters.msa && appState.ui.reportType === "depositMarketShare"
          ? appState.filters.msa.toString()
          : "",
      formKey: "msa",
      static: true,
    },
    {
      label: "",
      value:
        appState.ui.institutionType === "bhcs" &&
        appState.ui.reportType === "depositMarketShare"
          ? "Holding Companies"
          : "",
      formKey: "hcr",
      static: true,
    },
    {
      label: "ZIP",
      value: appState.filters.zipbr,
      formKey: "zipbr",
      static: false,
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={marketShareSearchPills} />}
    />
  );

  // removes MSAs pill on Bank Name search
  React.useEffect(() => {
    if (appState.search.name && appState.ui.displayResults === "msas") {
      updateUi("displayResults", "statesByCounty");
    }
  }, [appState.search.name, appState.ui.displayResults, updateUi]);

  const [updateFormField, setUpdateFormField] = React.useState("");
  React.useEffect(() => {
    if (appState.filters.cert) {
      setUpdateFormField("name");
    }
    if (appState.filters.bhcRSSD) {
      setUpdateFormField("bhcName");
    }
    if (appState.filters.name) {
      setUpdateFormField("cert");
    }
    if (appState.filters.bhcName) {
      setUpdateFormField("bhcRSSD");
    }
  }, [
    appState.filters.bhcName,
    appState.filters.bhcRSSD,
    appState.filters.cert,
    appState.filters.name,
    appState.filters,
  ]);

  const defaultDisplay = (
    <>
      <Grid container spacing={2}>
        <Grid size={11}>
          <Typography id="market-share-description" className={classes.body}>
            The Deposit Market Share is the percentage of deposits an
            FDIC-insured institution has within a defined geographic market.
            This yearly data is based on the annual Summary of Deposits (SOD)
            survey for FDIC-insured institutions as of June 30.
            <br />
            <br />
            The Deposit Market Share and the Pro Forma (HHI) Reports provide
            information for all institutions within a specific geographic market
            for a specific time period. The Market Presence and Growth Rate
            Reports provide similar information, but from the perspective of one
            institution. All reports provide data back to 1994 and are available
            by institution or bank holding company.
          </Typography>
          <Typography
            id="market-share-report-description"
            className={classes.body}
            component="div"
          >
            <ul>
              <li>
                <b>Deposit Market Share Report</b> provides a summary of all
                FDIC-insured institutions, the number of branches, the amount of
                deposits, and the percentage of their market share for any
                specified geographic area (state, county, city, and ZIP code).
                User-defined custom markets can be created by specifying
                multiple states, counties, cities, and/or ZIP codes.
              </li>

              <br />
              <li>
                <b>Pro-Forma Report</b> offers the ability to see the before and
                after effects of proposed mergers or acquisitions within a
                specified geographic market. After selecting the geographic
                market, the user must select the acquiring institution and the
                divested institution or branches of an institution in order to
                get the results. The results include the before (Old Bank) and
                after (New Bank) data with the summary of all FDIC-insured
                institutions within the geographic area and the number of
                branches, the amount of deposits, the market share, and{" "}
                <Typography
                  variant="body2"
                  id="market-share-report-link"
                  style={{ display: "inline" }}
                >
                  <Link href="https://www.justice.gov/atr/herfindahl-hirschman-index">
                    Herfindahl-Hirschman Index, HHI.
                  </Link>
                </Typography>{" "}
                User-defined custom markets can be created by specifying
                multiple states, counties, cities, and/or ZIP Code. Horizontal
                Merger Guidelines issued by the U.S. Department of Justice and
                the Federal Trade Commission. See Merger Guidelines § 1.51{" "}
                <Typography
                  variant="body2"
                  id="market-share-report-link"
                  style={{ display: "inline" }}
                >
                  <Link href="https://www.justice.gov/atr/public/guidelines/hmg-2010.html#5c">
                    https://www.justice.gov/atr/public/guidelines/hmg-2010.html#5c
                  </Link>
                </Typography>{" "}
                and{" "}
                <Typography
                  variant="body2"
                  id="market-share-report-link"
                  style={{ display: "inline" }}
                >
                  <Link href=" http://www.justice.gov/atr/herfindahl-hirschman-index">
                    {" "}
                    http://www.justice.gov/atr/herfindahl-hirschman-index.
                  </Link>
                </Typography>
              </li>

              <br />

              <li>
                <b>Market Presence Report</b> identifies where an institution
                operates geographically, by state, county, or Metropolitan
                Statistical Area (MSA), in a single report.
              </li>

              <br />
              <li>
                <b>Growth Rate Report</b> provides the ability to compare two
                years of data for any FDIC-insured institution or bank holding
                company by state, county, or MSA.
              </li>
            </ul>
          </Typography>
        </Grid>
      </Grid>
    </>
  );
  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle="Market Share" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <Root>
        <AppBody
          introText={introParagraph}
          searchForm={<MarketShareSearchForm />}
          backButton={
            <BackButton app="SOD" buttonLabel="Back to SOD" variant="dark" />
          }
        >
          <BackToResultsButton updateFormField={updateFormField} />
          {appState.ui.reportType === "marketPresence" &&
            appState.filters.lastYear &&
            (appState.search.name || appState.search.bhcName) && (
              <DepositsNameSearchTable searchPills={searchPills} />
            )}
          {appState.filters.lastYear &&
            (appState.filters.cert || appState.filters.bhcRSSD) &&
            appState.ui.reportType === "marketPresence" && (
              <MarketPresenceTables
                appState={appState}
                updateUi={updateUi}
                searchPills={searchPills}
              />
            )}
          {appState.filters.lastYear &&
            (appState.filters.stateNames[0] ||
              appState.filters.msa ||
              appState.filters.zipbr) &&
            appState.ui.reportType === "depositMarketShare" && (
              <DepositMarketShareTable
                appState={appState}
                updatePagination={updatePagination}
                searchPills={searchPills}
              />
            )}
          {appState.filters.lastYear &&
            appState.ui.locations === "countyByState" &&
            appState.filters.stalpbr &&
            appState.ui.reportType === "depositMarketShare" && (
              <DepositMarketShareCountySummaryByState />
            )}

          {appState.ui.reportType === "proForma" &&
            appState.filters.lastYear &&
            (appState.filters.msa ||
              appState.filters.zipbr ||
              appState.filters.stateNames[0]) && <ProFormaTable />}
          {appState.ui.reportType === "growthRate" &&
            (appState.filters.cert ||
              appState.filters.bhcRSSD ||
              appState.search.name ||
              appState.search.bhcName) &&
            appState.filters.startDate &&
            appState.filters.endDate && <GrowthRate />}
          {!appState.filters.cert &&
            !appState.search.name &&
            !appState.search.bhcName &&
            !appState.filters.bhcRSSD &&
            !appState.filters.stateNames[0] &&
            !appState.filters.stalpbr &&
            !appState.filters.msa &&
            !appState.filters.zipbr &&
            defaultDisplay}
        </AppBody>
      </Root>
    </>
  );
}
